/**
 * Prospectors: IPM's managers (GDD §3.8). Recruited with Stellar Cores,
 * assigned one per planet, permanent. A duplicate recruit levels the one you
 * have. The Classroom room amplifies every bonus.
 */

export type ProspectorRole = 'mine' | 'ship' | 'cargo';
export type ProspectorRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface ProspectorDef {
  readonly id: string;
  readonly name: string;
  readonly role: ProspectorRole;
  readonly rarity: ProspectorRarity;
}

export const PROSPECTOR_ROLE_LABEL: Readonly<Record<ProspectorRole, string>> = {
  mine: 'Driller', ship: 'Pilot', cargo: 'Stevedore',
};

export const PROSPECTOR_RARITY_COLOR: Readonly<Record<ProspectorRarity, string>> = {
  common: '#a9b8d6', rare: '#4dd9f5', epic: '#9b7bff', legendary: '#f5c542',
};

/** Planet-stat bonus at level 1, by rarity. */
export const PROSPECTOR_BASE: Readonly<Record<ProspectorRarity, number>> = {
  common: 0.5, rare: 1, epic: 2, legendary: 4,
};
/** Each level after the first adds this share of the base. */
export const PROSPECTOR_LEVEL_STEP = 0.25;
export const PROSPECTOR_MAX_LEVEL = 10;

export const PROSPECTOR_ODDS: Readonly<Record<ProspectorRarity, number>> = {
  common: 0.6, rare: 0.28, epic: 0.1, legendary: 0.02,
};

export const RECRUIT_COST_SC = 20;

export const PROSPECTORS: readonly ProspectorDef[] = [
  { id: 'wren', name: 'Wren Kestrel', role: 'mine', rarity: 'common' },
  { id: 'osk', name: 'Osk Halloran', role: 'ship', rarity: 'common' },
  { id: 'mira', name: 'Mira Tolle', role: 'cargo', rarity: 'common' },
  { id: 'juno', name: 'Juno Varga', role: 'mine', rarity: 'common' },
  { id: 'teo', name: 'Teo Brand', role: 'ship', rarity: 'rare' },
  { id: 'ilsa', name: 'Ilsa Morrow', role: 'cargo', rarity: 'rare' },
  { id: 'cass', name: 'Cass Rehn', role: 'mine', rarity: 'rare' },
  { id: 'dax', name: 'Dax Orrin', role: 'ship', rarity: 'epic' },
  { id: 'pell', name: 'Pell Quarry', role: 'mine', rarity: 'epic' },
  { id: 'sable', name: 'Sable Ono', role: 'cargo', rarity: 'epic' },
  { id: 'nyx', name: 'Nyx Arden', role: 'mine', rarity: 'legendary' },
  { id: 'bram', name: 'Bram Kettle', role: 'cargo', rarity: 'legendary' },
];

export function prospectorDef(id: string): ProspectorDef {
  const def = PROSPECTORS.find((p) => p.id === id);
  if (!def) throw new Error(`Unknown prospector: ${id}`);
  return def;
}

export function prospectorBonus(def: ProspectorDef, level: number): number {
  return PROSPECTOR_BASE[def.rarity] * (1 + PROSPECTOR_LEVEL_STEP * (Math.max(1, level) - 1));
}
